import React, {
  useEffect,
  useState,
} from "react";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCircleNotch } from "@fortawesome/free-solid-svg-icons";
import { BlobProvider } from "@react-pdf/renderer";
import Pagination from "react-js-pagination";
import { format } from "date-fns";
import { toast } from "react-toastify";

import AvaliacaoGeneticaService from "../services/AvaliacaoGeneticaService";
import { PDFDocument } from "./pdfIndividual";

// Bug preexistente corrigido aqui: esta tela sempre tentou ler r.valor /
// r.acc / r.top de cada animal, mas o backend NUNCA devolveu esses nomes -
// confirmado direto contra a API (POST consultarAnimaisAvaliacaoGenetica):
// para campoOrdem "MGT" o retorno tem os campos "mgt" (valor), "amgt"
// (acurácia) e "tmgt" (top%), com dados reais e corretos (ex: mgt:31.2,
// amgt:25, tmgt:0.1) - so os NOMES que a tela procurava estavam errados,
// por isso vinha tudo em branco. Cada DEP tem seu proprio trio de campos no
// DTO do backend (ver /ANCP/Models/AnimaisFiltradosAvaliacaoGeneticaResposta.cs):
// a familia MGT (MGT/MGTCR/MGTRE/MGTNC_CAR/MGTF1_CAR) usa prefixo A.../T...
// (ex: AMGT/TMGT), e todas as outras DEPs (DIPP, D3P, DPG, etc.) usam
// prefixo ACC_.../TOP_... (ex: ACC_DIPP/TOP_DIPP). As funcoes abaixo
// resolvem dinamicamente, pelo campoOrdem escolhido na busca, qual campo
// do retorno da API usar para Valor/Acurácia/Top de cada linha - sem
// precisar mapear os ~50 DEPs um a um, e funcionando mesmo com as
// variacoes de maiusculas/minusculas que o JSON do backend usa (ex:
// "mgtnC_CAR", "d3P").
function normalizarChaveDep(chave) {
  return String(chave || "").replace(/[^a-zA-Z0-9]/g, "").toLowerCase();
}

function indiceDeChaves(objeto) {
  const indice = {};
  Object.keys(objeto || {}).forEach((chave) => {
    indice[normalizarChaveDep(chave)] = chave;
  });
  return indice;
}

function buscarPrimeiroCampo(objeto, indice, candidatos) {
  for (const candidato of candidatos) {
    const chaveReal = indice[normalizarChaveDep(candidato)];
    if (chaveReal !== undefined) return objeto[chaveReal];
  }
  return undefined;
}

function getValoresDepAnimal(animal, campoOrdem) {
  if (!animal || !campoOrdem) return { valor: undefined, acc: undefined, top: undefined };
  const indice = indiceDeChaves(animal);
  const codigo = String(campoOrdem);
  return {
    valor: buscarPrimeiroCampo(animal, indice, [codigo]),
    acc: buscarPrimeiroCampo(animal, indice, ["ACC_" + codigo, "A" + codigo]),
    top: buscarPrimeiroCampo(animal, indice, ["TOP_" + codigo, "T" + codigo]),
  };
}

export default function Table({
  initialConsulta,
  initialResult,
  handleAnimal,
  handleCurrentPage,
  racas,
  sexos,
  categorias,
  variedades,
  totalAnimais
}) {
  const [activePage, setActivePage] = useState(initialConsulta.Page);
  const [result, setResult] = useState([]);
  const [consulta, setConsulta] = useState(initialConsulta);

  const [isLoading, setIsLoading] = useState(false);

  const [animaisId, setAnimaisId] = useState([]);
  const [animais, setAnimais] = useState([]);

  useEffect(() => {
    setResult(initialResult);
  }, [initialResult]);

  useEffect(() => {
    setConsulta(initialConsulta);
  }, [initialConsulta]);

  const handlePageChange = (pageNumber) => {
    (animais.length > 0) && setAnimais([])
    setActivePage(pageNumber);
  };

  useEffect(() => {
    handleCurrentPage(activePage);
    // eslint-disable-next-line
  }, [activePage]);

  let perPage =
    consulta && consulta.AnimaisPorPagina
      ? parseInt(consulta.AnimaisPorPagina)
      : 0;

  const campoOrdem = localStorage.getItem("campo_ordem");
  const opcoesFiltro = localStorage.getItem("opcoes_filtro").split(",");

  const handleGerarPDF = async () => {
    
    if(animaisId.length === 0 || isLoading) return 
    setIsLoading(true);
    
    const animais = await Promise.all(
      animaisId.map(async (id) => {
        const animal = {};
        try {
          animal.detalhesAnimal = await AvaliacaoGeneticaService.detalhesAnimal(id);

          const { caracteristicasAgrupadas } = await AvaliacaoGeneticaService.helpersCaracteristicas(animal.detalhesAnimal.caracteristicas)
          const agAtual = await AvaliacaoGeneticaService.agAtual(animal.detalhesAnimal.animal.idRaca)

          const newModel = changeModel({
            ...animal.detalhesAnimal,
            caracteristicasAgrupadas,
            agAtual,
          })

          return newModel;
        } catch (error) {
          console.error(error);
        }
      })
    );
    const validateItems = animais.filter(item => item !== undefined)
    setAnimais(validateItems);

    return
  }


  const changeModel = (data) => {
    const { animal, agAtual, caracteristicas, idCentral, genealogia, caracteristicasAgrupadas } = data

    const _genealogias = Object.keys(genealogia).map((key, i) => {
      const title = (key === 'avoPai' && 'Avô Pai')
        || (key === 'avomPai' && 'Avó Pai')
        || (key === 'pai' && 'Pai')
        || (key === 'mae' && 'Mãe')
        || (key === 'avoMae' && 'Avô Mãe')
        || (key === 'avomMae' && 'Avó Mãe')

      const bgColor = (key === 'avoPai' || key === 'pai' || key === 'avoMae') && 'blue'
        || (key === 'avomMae' || key === 'avomPai' || key === 'mae') && 'pink'

      if (key !== 'animal') {
        return {
          key: key,
          bgColor: bgColor,
          title: title,
          nome: genealogia[key].nome || '---',
          serie: genealogia[key].serie || '---',
          rgn: genealogia[key].rgn || '---',
          rgd: genealogia[key].rgd || '---',
          valor: `MGTe: ${genealogia[key].mgt || '---'}`,
          top: `TOP: ${genealogia[key].tmgt + '%' || '---'}`
        }
      }
    })

    _genealogias.shift()

    const model = {
      cga: animal.cga,
      title: agAtual.descricao || '---',
      nome: animal.nome || '---',
      proprietario: animal.nomeProprietario || '---',
      criador: animal.nomeCriador || '---',
      section_01: [
        {
          title: 'Série:',
          value: animal.serie || '---'
        },
        {
          title: 'RGN:',
          value: animal.rgn || '---'
        },
        {
          title: 'RGD:',
          value: animal.rgd || '---'
        },
        {
          title: 'Data de nascimento:',
          value: animal.dtNascimento ? format(new Date(animal.dtNascimento), "dd-MM-yyyy") : '---'
        },
        {
          title: 'Situação:',
          value: animal.situacao || '---'
        },
        {
          title: 'Reprodução programada:',
          value: animal.reproducaoProgramada || '---'
        }
      ],
      section_02: [
        {
          title: 'MGTe:',
          values: [
            {
              caract: caracteristicas.mgt || '---'
            },
            {
              caract: `@ ${caracteristicas.amgt}` || '---'
            },
            {
              caract: `® ${caracteristicas.tmgt || '---'}%`
            }
          ]
        },
        {
          title: 'Raça:',
          value: animal.nomeRaca || '---'
        },
        {
          title: 'Categoria:',
          value: animal.categoriaDescricao || '---'
        },
        {
          title: 'Variedade:',
          value: animal.variedadeDescricao || '---'
        },
        {
          title: 'Sexo:',
          value: animal.sexoDescricao || '---'
        },
        {
          title: 'Central:',
          value: idCentral || '---'
        },

      ],
      genealogia: _genealogias,
      caracteristicas,
      caracteristicasAgrupadas,
      classification: [
        {
          key: 'diamante',
          str01: 'Diamante',
          str02: '- Top 1%'
        },
        {
          key: 'esmeralda',
          str01: 'Esmeralda',
          str02: '- Top 5%'
        },
        {
          key: 'rubi',
          str01: 'Rubi',
          str02: '- Top 10%'
        },
        {
          key: 'safira',
          str01: 'Safira',
          str02: '- Top 20%'
        },
        {
          key: 'topazio',
          str01: 'Topázio Imperial',
          str02: '- Top 30%'
        },
        {
          key: 'top',
          str01: 'TOP Maiores',
          str02: '- Top 30%'
        }
      ]
    }

    return model
  }

  return (
    <>
      <div className="d-flex justify-content-end mt-4">
        <button
          className="global-btn-info shadow-none justify-content-center"
          style={{ width: 211, marginRight: 20 }}
        >
          Imprimir listagem
        </button>

        <button
          className="global-btn-outline shadow-none justify-content-center"
          style={{ width: 270 }}
        >
          Imprimir fichas genéticas
        </button>
      </div>

      <div className="table-horizontal">
        <div className="table-horizontal-header bg-primary">
          <h2 className="ff-roboto fs-22px">Animais filtrados</h2>

          {(animais.length === 0) && (
            <button className={`global-btn-info ${isLoading && "disabled"}`} onClick={handleGerarPDF}>
              {isLoading ? (
                <>Carregando Arquivos... <FontAwesomeIcon icon={faCircleNotch} pulse /></>
              ) : (
                `Gerar PDF (${animaisId.length}) items selecionados`
              )}
            </button>
          )}

          {animais.length > 0 && (
            <BlobProvider
              document={
                <PDFDocument
                  detalhesAnimal={animais}
                />
              }
              fileName="Ficha Animal Múltiplos"
            >
              {({ url, loading, error }) => {
                if (error) {
                  console.error(error)
                }
                if (animais.length > 0 && loading) {
                  return (
                    <button className={`global-btn-info`}>
                      <>Criando PDF... <FontAwesomeIcon icon={faCircleNotch} pulse /></>
                    </button>
                  )
                } else {
                  setIsLoading(false);

                  if(isLoading && animais.length < animaisId.length) {
                    toast.error('Não foi possível gerar PDF de alguns itens pois não foram encontrados na base de dados!')
                  }              

                  return (
                    <a
                      className={`global-btn-info`}
                      href={url}
                      target='_blank'
                    >
                      {(animais.length > 0 && loading) ? (
                        <FontAwesomeIcon icon={faCircleNotch} pulse />
                      ) : "Abrir PDF"}
                    </a>
                  )
                }
              }
              }
            </BlobProvider >
          )}
        </div>
        <div className="table-filter__scroll">
          <div className="table-horizontal-body">
            <div className="table-horizontal-row-item-header py-0">
              <div className="table-horizontal-row-item-header-item" style={{ padding: 0 }}>
                <span>
                  <div className="table-horizontal-row-item" style={{ padding: 0 }}>
                    <div className="table-remove__input-container">
                      <input
                        className="table-remove__input"
                        type="checkbox"
                        style={{ margin: "0px" }}
                        onChange={(event) => {
                          setAnimais([])
                          if (event.target.checked) {
                            const AllItems = result.map(item => item.cga)
                            setAnimaisId(AllItems)
                          } else {
                            setAnimaisId([])
                          }
                        }}
                      />
                      <span className="table-remove__input-custom"></span>
                    </div>
                  </div>

                </span>
              </div>
              <div className="table-horizontal-row-item-header-item">
                <span>Nome do animal</span>
              </div>
              <div className="table-horizontal-row-item-header-item">
                <span>Série</span>
              </div>
              <div className="table-horizontal-row-item-header-item">
                <span>RGN</span>
              </div>
              <div className="table-horizontal-row-item-header-item">
                <span>RGD</span>
              </div>
              <div className="table-horizontal-row-item-header-item">
                <span>Data Nasc</span>
              </div>
              <div className="table-horizontal-row-item-header-item">
                <span>{campoOrdem}</span>
              </div>
              <div className="table-horizontal-row-item-header-item">
                <span>Acc</span>
              </div>
              <div className="table-horizontal-row-item-header-item">
                <span>Top</span>
              </div>
              <div className="table-horizontal-row-item-header-item">
                <span>Sexo</span>
              </div>
              <div className="table-horizontal-row-item-header-item">
                <span>Raça</span>
              </div>
              <div className="table-horizontal-row-item-header-item">
                <span>Cat</span>
              </div>
              <div className="table-horizontal-row-item-header-item">
                <span>Var</span>
              </div>
              {!!opcoesFiltro[0] ? (
                <div className="table-horizontal-row-item-header-item">
                  <span>{opcoesFiltro[0]}</span>
                </div>
              ) : null}
              {!!opcoesFiltro[1] ? (
                <div className="table-horizontal-row-item-header-item">
                  <span>{opcoesFiltro[1]}</span>
                </div>
              ) : null}
              {!!opcoesFiltro[2] ? (
                <div className="table-horizontal-row-item-header-item">
                  <span>{opcoesFiltro[2]}</span>
                </div>
              ) : null}
              {opcoesFiltro[3] ? (
                <div className="table-horizontal-row-item-header-item">
                  <span>{opcoesFiltro[3]}</span>
                </div>
              ) : null}
            </div>
            {result &&
              result.length > 0 &&
              result.map((r, index) => {
                let dataNascimento =
                  r && r.dataNascimento ? r.dataNascimento : "";

                if (dataNascimento !== "") {
                  dataNascimento = format(
                    new Date(dataNascimento),
                    "dd-MM-yyyy"
                  );
                }

                let nomeSexo = "";
                if (r && r.sexoId) {
                  let sexo = sexos.filter((s) => s.id === r.sexoId);
                  if (sexo && sexo.length > 0) {
                    nomeSexo = sexo[0].nome;
                  }
                }

                let nomeRaca = "";
                if (r && r.racaId) {
                  let raca = racas.filter((s) => s.id === r.racaId);
                  if (raca && raca.length > 0) {
                    nomeRaca = raca[0].nome;
                  }
                }

                let nomeVariedade = "";
                if (r && r.variedade) {
                  let variedade = variedades.filter(
                    (s) => s.codigo === r.variedade
                  );
                  if (variedade && variedade.length > 0) {
                    nomeVariedade = variedade[0].nome;
                  }
                }

                let nomeCategoria = "";
                if (r && r.categoria) {
                  let categoria = categorias.filter(
                    (s) => s.codigo === r.categoria
                  );
                  if (categoria && categoria.length > 0) {
                    nomeCategoria = categoria[0].nome;
                  }
                }

                const valoresDep = getValoresDepAnimal(r, campoOrdem);

                return (
                  <div
                    key={r.cga}
                    className="table-horizontal-row"
                    style={{ background: index % 2 === 0 ? "#F7F7FA" : "#FFF" }}
                  >
                    <div className="table-horizontal-row-item">
                      <div className="table-remove__input-container">
                        <input
                          className="table-remove__input"
                          type="checkbox"
                          style={{ margin: "0px" }}
                          value={animaisId.some(
                            (animalId) => animalId === r.cga
                          )}
                          checked={animaisId.some(
                            (animalId) => (animalId === r.cga) ? true : false
                          )}
                          onChange={(event) => {
                            if (event.target.checked) {

                              if (animaisId.length === 15) {
                                toast.warn(`Você pode gerar no máximo ${15} PDF's por vez!`)
                                return
                              } 
                              setAnimaisId([...animaisId, r.cga]);
                            } else {
                               
                              setAnimaisId(
                                animaisId.filter(
                                  (animalId) => animalId !== r.cga
                                )
                              );
                            }
                            (animais.length > 0) && setAnimais([])
                          }}
                        />
                        <span className="table-remove__input-custom"></span>
                      </div>
                    </div>
                    <div
                      className="table-horizontal-row-item item-cursor-pointer"
                      onClick={() => handleAnimal(r.cga)}
                    >
                      <span>{r && r.nome}</span>
                    </div>
                    <div
                      className="table-horizontal-row-item item-cursor-pointer"
                      onClick={() => handleAnimal(r.cga)}
                    >
                      <span>{r && r.serie}</span>
                    </div>
                    <div
                      className="table-horizontal-row-item item-cursor-pointer"
                      onClick={() => handleAnimal(r.cga)}
                    >
                      <span>{r && r.rgn}</span>
                    </div>
                    <div
                      className="table-horizontal-row-item item-cursor-pointer"
                      onClick={() => handleAnimal(r.cga)}
                    >
                      <span>{r && r.rgd}</span>
                    </div>
                    <div
                      className="table-horizontal-row-item item-cursor-pointer"
                      onClick={() => handleAnimal(r.cga)}
                    >
                      <span>{dataNascimento}</span>
                    </div>
                    <div
                      className="table-horizontal-row-item item-cursor-pointer"
                      onClick={() => handleAnimal(r.cga)}
                    >
                      <span>{valoresDep.valor}</span>
                    </div>
                    <div
                      className="table-horizontal-row-item item-cursor-pointer"
                      onClick={() => handleAnimal(r.cga)}
                    >
                      <span>{valoresDep.acc}</span>
                    </div>
                    <div
                      className="table-horizontal-row-item item-cursor-pointer"
                      onClick={() => handleAnimal(r.cga)}
                    >
                      <span>{valoresDep.top}%</span>
                    </div>
                    <div
                      className="table-horizontal-row-item item-cursor-pointer"
                      onClick={() => handleAnimal(r.cga)}
                    >
                      <span>{nomeSexo}</span>
                    </div>
                    <div
                      className="table-horizontal-row-item item-cursor-pointer"
                      onClick={() => handleAnimal(r.cga)}
                    >
                      <span>{nomeRaca}</span>
                    </div>
                    <div
                      className="table-horizontal-row-item item-cursor-pointer"
                      onClick={() => handleAnimal(r.cga)}
                    >
                      <span>{nomeCategoria}</span>
                    </div>
                    <div
                      className="table-horizontal-row-item item-cursor-pointer"
                      onClick={() => handleAnimal(r.cga)}
                    >
                      <span>{nomeVariedade}</span>
                    </div>
                    {!!opcoesFiltro[0] ? (
                      <div
                        className="table-horizontal-row-item item-cursor-pointer"
                        onClick={() => handleAnimal(r.cga)}
                      >
                        <span>{r && r.nF120}</span>
                      </div>
                    ) : null}
                    {!!opcoesFiltro[1] ? (
                      <div
                        className="table-horizontal-row-item item-cursor-pointer"
                        onClick={() => handleAnimal(r.cga)}
                      >
                        <span>{r && r.nR120}</span>
                      </div>
                    ) : null}

                    {!!opcoesFiltro[2] ? (
                      <div
                        className="table-horizontal-row-item item-cursor-pointer"
                        onClick={() => handleAnimal(r.cga)}
                      >
                        <span>{r && r.nF450}</span>
                      </div>
                    ) : null}
                    {opcoesFiltro[3] ? (
                      <div
                        className="table-horizontal-row-item item-cursor-pointer"
                        onClick={() => handleAnimal(r.cga)}
                      >
                        <span>{r && r.nR450}</span>
                      </div>
                    ) : null}
                  </div>
                );
              })}
          </div>
        </div>
      </div>

      <div className="table-remove__footer justify-content-between">
        <span className="ff-roboto fs-18px fw-bold">
          Total {perPage * activePage} de {totalAnimais}
        </span>

        <Pagination
          activePage={consulta.Page}
          itemsCountPerPage={perPage}
          totalItemsCount={totalAnimais}
          pageRangeDisplayed={5}
          onChange={handlePageChange}
          itemClass="page-item-pagination"
          linkClass="page-link-pagination"
        />
      </div>
    </>
  );
}
